// The content security policy we use for playback iframes. Keep this in sync
// with the settings for 's/playback/iframe.html' in 'appengine/app/app.yaml'.
// (Once the playback service worker is turned on everywhere, we can remove the
// 'app.yaml' entry, and this will be the canonical place for defining this
// policy.)
export const playbackIFrameCspPolicy: string = `form-action 'none'; frame-src 'self'; manifest-src 'none'; object-src 'none'; sandbox allow-same-origin allow-scripts; script-src 'none'; worker-src 'none'; report-to csp-endpoint; report-uri https://services.fullstory.com/cspviolations/cspReport`;

/**
 * @returns a synthesize playback iframe response if the provided URL represents
 * a playback iframe document, or undefined otherwise.
 */
export function maybeSynthesizePlaybackIFrame(href: string): Response | undefined {
  // Do a fast reject using a substring match before we parse the URL.
  if (!href.includes('/s/playback/iframe.html')) {
    return undefined;
  }

  const url = new URL(href);
  if (url.origin !== self.location.origin) {
    return undefined;
  }
  if (url.pathname !== '/s/playback/iframe.html') {
    return undefined;
  }

  return synthesizePlaybackIFrame(url);
}

/**
 * Synthesize a playback iframe document based on the options in the request URL
 * and respond with it to the client.
 */
export function synthesizePlaybackIFrame(requestUrl: URL): Response {
  const doctype = validateDoctype(requestUrl);
  console.debug(`Synthesizing playback iframe with doctype '${doctype}'.`);
  return new Response(doctype, {
    status: 200,
    statusText: 'OK',
    headers: {
      'Content-Type': 'text/html',
      'Content-Length': doctype.length.toString(),
      'Content-Security-Policy': playbackIFrameCspPolicy,
    },
  });
}

const html5Doctype: string = `<!DOCTYPE html>`;
const quirksModeDoctype: string = ``;

// Examine the doctype requested by the client, if any, and make sure it looks
// like a valid doctype. We don't want the doctype to be a route for script
// injection. Returns the final doctype that should used in the response to the
// client.
function validateDoctype(requestUrl: URL): string {
  const doctypeParam = requestUrl.searchParams.get('doctype');
  const baseUrl = requestUrl.searchParams.get('baseurl') ?? 'about:blank';

  // Use an HTML5 doctype by default.
  if (doctypeParam === null) {
    return html5Doctype;
  }

  return normalizeDoctype(doctypeParam, baseUrl);
}

/**
 * @returns a normalized version of the requested doctype. To ensure that the
 * doctype isn't a route for code injection, all quirks mode doctypes are
 * normalized to a known-safe canonical form.
 */
export function normalizeDoctype(requestedDoctype: string, baseUrl: string): string {
  const doctype = requestedDoctype.trim();

  // An empty doctype is fine.
  if (doctype === '') {
    return doctype;
  }

  // Allow a trivial doctype with no DTD. (This includes the HTML5 doctype.)
  if (isValidTrivialDoctype(doctype)) {
    return doctype;
  }

  // Allow valid, complete doctypes with external DTDs.
  let possiblyReplacedDoctype: string | undefined;
  if ((possiblyReplacedDoctype = normalizePublicExternalDTD(doctype, baseUrl))) {
    return possiblyReplacedDoctype;
  }

  // Allow valid doctypes with private external DTDs.
  if ((possiblyReplacedDoctype = normalizePrivateExternalDTD(doctype, baseUrl))) {
    return possiblyReplacedDoctype;
  }

  // This doctype either includes an internal DTD, which is incredibly rare for
  // HTML and is tricky to validate because it includes an entire embedded DTD
  // document, or is just malformed. Don't allow the original doctype through,
  // but return a quirks mode doctype. Malformed doctypes trigger quirks mode,
  // and while a doctype with an internal DTD may or may not, that case is too
  // rare to worry about.
  return quirksModeDoctype;
}

// Returns true if the provided doctype is trivial - i.e., it doesn't specify a
// DTD at all.
export function isValidTrivialDoctype(doctype: string): boolean {
  return /^<!DOCTYPE\s+[\w:]+\s*>$/i.test(doctype);
}

/**
 * If doctype is valid and uses a public external DTD, returns the normalize doctype;
 * otherwise, returns undefined.
 *
 * If doctype contains a relative path and baseUrl is provided, the normalized doctype
 * will have the url replaced with an absolute url (constructed from relative path and
 * the provided baseUrl)
 *
 * We are a bit more suspicious than the real HTML parser would be in that we don't
 * allow angle brackets, even inside quotes. This is to minimize the odds that
 * someone can find a clever way to confuse this check and terminate the doctype
 * tag early, allowing them to inject something sketchy using the rest of the
 * doctype string.
 *
 * @param doctype doctype recorded
 * @param baseUrl recorded base url of the frame
 */
export function normalizePublicExternalDTD(doctype: string, baseUrl: string): string | undefined {
  const match = /^<!DOCTYPE\s+[\w:]+\s+PUBLIC\s+"[^<>"]*"(\s+"([^<>"]*)")?\s*>$/i.exec(doctype);
  if (!match) {
    return undefined;
  }

  // If no URL was specified, no further validation is needed.
  if (!match[2]) {
    return doctype;
  }

  return normalizeDoctypeWithDTDUrl(doctype, match[2], baseUrl);
}

// As above, but for private external DTDs.
export function normalizePrivateExternalDTD(doctype: string, baseUrl: string): string | undefined {
  const match = /^<!DOCTYPE\s+[\w:]+\s+SYSTEM\s+"([^<>"]*)"\s*>$/i.exec(doctype);
  if (!match || !match[1]) {
    return undefined;
  }

  return normalizeDoctypeWithDTDUrl(doctype, match[1], baseUrl);
}

function normalizeDoctypeWithDTDUrl(
  doctype: string,
  url: string,
  baseUrl: string,
): string | undefined {
  // if specified DTD href is already an absolute url without the base url
  if (normalizeValidDTDUrl(url, 'about:blank')) {
    return doctype;
  }

  // if the specifed DTD is a relative url
  const fullPath = normalizeValidDTDUrl(url, baseUrl);
  if (fullPath) {
    return doctype.replace(url, fullPath);
  }

  return undefined;
}

// Returns full href if the DTD URL is valid and served via HTTP or HTTPS, undefined otherwise
function normalizeValidDTDUrl(dtdHref: string, baseUrl: string): string | undefined {
  try {
    const dtdUrl = new URL(dtdHref, baseUrl);
    return dtdUrl.protocol === 'http:' || dtdUrl.protocol === 'https:' ? dtdUrl.href : undefined;
  } catch (e) {
    return undefined;
  }
}
